import React, { useEffect, useMemo, useState } from 'react';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useCreatePushNotificationMutation } from 'shared/hooks/queries/use-create-push-notification-mutation';
import { usePushNotificationMessageQuery } from 'shared/hooks/queries/use-push-notification-message-query';
import { useSetPushNotificationMessageMutation } from 'shared/hooks/queries/use-set-push-notification-message-mutation';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { PushNotificationMessageRequestState } from 'shared/services/local-storage';

import { RequestNotificationMessage } from './request-notification-message';
import {
  RequestPushNotificationsContext,
  RequestPushNotificationsContextProps,
} from './request-push-notifications-context';
import { useDailyPushNotificationCheck } from './use-daily-push-notification-check';

export type RequestPushNotificationsProviderProps = {
  children: React.ReactNode;
};

export const RequestPushNotificationsProvider: React.FC<
  RequestPushNotificationsProviderProps
> = ({ children }) => {
  const locale = useI18nLocale();
  const [messageOpen, setMessageOpen] = useState<boolean>(false);
  const [permission] = useNotificationPermission();
  const { data: cachedPushToken } = useCachedPushNotificationTokenQuery();

  const user = useUser();
  const { data: messageState } = usePushNotificationMessageQuery(user?.uid);
  const { mutate: setMessageState } = useSetPushNotificationMessageMutation();
  const { mutate: requestPermission } = useCreatePushNotificationMutation();
  useDailyPushNotificationCheck();

  const closeMessage = () => {
    if (!user?.uid) {
      return;
    }

    setMessageState({
      uid: user?.uid,
      state: PushNotificationMessageRequestState.Asked,
    });

    setMessageOpen(false);
  };

  const onSubmit = async () => {
    if (!user?.uid) {
      return;
    }
    requestPermission({
      userId: user.uid,
      locale: user.settings?.language ?? locale,
    });
    setMessageState({
      uid: user.uid,
      state: PushNotificationMessageRequestState.Asked,
    });
    setMessageOpen(false);
  };

  const value = useMemo<RequestPushNotificationsContextProps>(() => ({}), []);

  useEffect(() => {
    if (permission === 'granted' && !!cachedPushToken) {
      setMessageOpen(false);
      return;
    }

    if (
      permission === 'prompt' &&
      messageState !== PushNotificationMessageRequestState.Asked &&
      (user?.user_metadata?.completed_onboarding_november_2024 ||
        user?.user_metadata?.completed_onboarding_questions)
    ) {
      setMessageOpen(true);
      return;
    }

    if (
      permission === 'granted' &&
      !cachedPushToken &&
      messageState !== PushNotificationMessageRequestState.Asked
    ) {
      setMessageOpen(true);
    }
  }, [
    cachedPushToken,
    messageState,
    permission,
    user?.user_metadata?.completed_onboarding_november_2024,
    user?.user_metadata?.completed_onboarding_questions,
  ]);

  return (
    <RequestPushNotificationsContext.Provider value={value}>
      {children}
      {messageOpen && (
        <RequestNotificationMessage
          onCancel={closeMessage}
          onSubmit={onSubmit}
        />
      )}
    </RequestPushNotificationsContext.Provider>
  );
};
